<template>
  <div class="d-flex flex-column h-100">

    <div class="d-flex flex-column gap-3">
      <CommentInput :commit="commit" class="mx-3" />

      <div :class="['comment-source-pills border-top border-bottom d-flex align-items-center flex-wrap', {shadow:scrollTop > 10}]">
        <div class="d-flex flex-row align-items-center w-100 px-3 py-2">
          <div class="section-title flex-grow-1 me-auto">
            Activity
          </div>
          <icon-button :class="['section-filter-toggle', {selected:showFilterPanel}]" small icon="filter" @click="showFilterPanel = !showFilterPanel" />
        </div>
        <BCollapse :show="showFilterPanel">
          <div>
            <div class="d-flex flex-column border-top px-3 py-2 gap-2">
              <div class="d-flex flex-row gap-1">
                <div class="section-filter-title">Show</div>
                <div class="d-flex flex-row flex-wrap gap-1">
                  <button
                    v-for="type in typeOptions"
                    :key="type.key"
                    type="button"
                    :class="['btn btn-sm comment-filter-pill', {selected:type.selected}]"
                    @click="toggleType(type.key)"
                  >
                    <span>
                      <i :class="`bi-${type.icon}`" />
                    </span>
                    <span>{{ type.label }}</span>
                  </button>
                </div>
              </div>
              <div class="d-flex flex-row gap-1 pt-2 border-top">
                <div class="section-filter-title">From</div>
                <div class="d-flex flex-row flex-wrap gap-1">
                  <button
                    v-for="source in sourceOptions"
                    :key="source.key"
                    type="button"
                    :disabled="!source.available"
                    :class="['btn btn-sm comment-filter-pill', {selected:source.selected}]"
                    @click="toggleSource(source.key)"
                  >
                    <span>
                      <i :class="`bi-${source.icon}`" />
                    </span>
                    <span>{{ source.label }}</span>
                    <spinner-view small v-if="source.loading" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </BCollapse>
      </div>
    </div>

    <div class="flex-grow-1 overflow-auto d-flex flex-column gap-3 pb-3" @scroll="onScroll">
      <div v-if="isLoadingVisibleComments" class="small p-3 d-flex flex-column align-items-center gap-1 text-dark">
        <SpinnerView />
        <div>Loading activity</div>
      </div>
      <div v-else-if="visibleComments.length === 0" class="text-center small text-muted p-3 fw-semibold">
        {{ emptyStateText }}
      </div>
      <template v-else>
        <div
          v-for="(item, index) in visibleComments"
          :key="item.key"
          :class="['px-3 pt-3' , {'border-top':index>0}]"
        >
          <CommentView
            :comment="item.comment"
            :source="item.sourceBadge"
            @deleted="() => onDeleted(item)"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {License} from "@/models/License";
import {Listing} from "@/models/Listing";
import {Property} from "@/models/Property";
import Comment from "@/models/ListingComment";
import SpinnerView from "@/components/common/SpinnerView.vue";
import CommentView from "@/components/common/CommentView.vue";
import {normalizeAddressCasing} from "@/addressFormatting";
import CommentInput from "@/components/common/CommentInput.vue";
import { CommentType } from "@strenforcement/domain/comment";
import IconButton from "@/components/common/IconButton.vue";
import BCollapse from "@/components/common/BCollapse.vue";
import {PersistentCityModel} from "@/models/PersistentCityModel";

const SOURCE_LICENSE = 'license';
const SOURCE_PROPERTY = 'property';
const SOURCE_LISTINGS = 'listings';
const KIND_LICENSE = 'license';
const KIND_PROPERTY = 'property';
const KIND_LISTING = 'listing';
const TYPE_STANDALONE = CommentType.STANDALONE;
const TYPE_VERIFICATION = CommentType.VERIFICATION;
const TYPE_DEFERRAL = CommentType.DEFERRAL;
const TYPE_CORRESPONDENCE = CommentType.CORRESPONDENCE;
const TYPE_LICENSE_EDIT = CommentType.LICENSE_EDIT;

const DEFAULT_DATA = () => ({
  licenseLookup: {},
  listingLookup: {},
  propertyLookup: {},
  selectedSources: {
    [SOURCE_LICENSE]: true,
    [SOURCE_PROPERTY]: true,
    [SOURCE_LISTINGS]: true
  },
  sourceLoading: {
    [SOURCE_LICENSE]: false,
    [SOURCE_PROPERTY]: false,
    [SOURCE_LISTINGS]: false
  },
  selectedTypes: {
    [TYPE_STANDALONE]: true,
    [TYPE_VERIFICATION]: true,
    [TYPE_CORRESPONDENCE]: true,
    [TYPE_LICENSE_EDIT]: true,
    [TYPE_DEFERRAL]: true
  },
  sourceLoaded: {
    [SOURCE_LICENSE]: false,
    [SOURCE_PROPERTY]: false,
    [SOURCE_LISTINGS]: false
  },
  sourceComments: {
    [SOURCE_LICENSE]: [],
    [SOURCE_PROPERTY]: [],
    [SOURCE_LISTINGS]: []
  }
});

export default {
  components: {BCollapse, IconButton, CommentInput, CommentView, SpinnerView},
  props: {
    model: PersistentCityModel
  },
  data() {
    return {
      showFilterPanel: false,
      scrollTop: 0,
      loadToken: 0,
      ...DEFAULT_DATA()
    };
  },
  watch: {
    activityKey: {
      immediate: true,
      async handler() {
        await this.resetAndLoad();
      }
    }
  },
  computed: {
    primaryModel() {
      return this.model || null;
    },
    activityContext() {
      const primary = this.primaryModel;
      const collectionName = primary?.constructor?.collectionName;
      if (collectionName === License.collectionName) {
        return {
          kind: KIND_LICENSE,
          primary,
          city: primary.city,
          propertyId: primary.propertyId || null,
          property: primary.property || null
        };
      }
      if (collectionName === Property.collectionName) {
        return {
          kind: KIND_PROPERTY,
          primary,
          city: primary.city,
          propertyId: primary.id || null,
          property: primary
        };
      }
      if (collectionName === Listing.collectionName) {
        return {
          kind: KIND_LISTING,
          primary,
          city: primary.city,
          propertyId: primary.verifiedPropertyId || null,
          property: primary.verifiedProperty || null
        };
      }
      return null;
    },
    activityKey() {
      const context = this.activityContext;
      if (!context) return '';
      return [
        context.kind,
        context.city?.id || '',
        context.primary?.id || '',
        context.propertyId || ''
      ].join(':');
    },
    city() {
      return this.activityContext?.city || null;
    },
    propertyId() {
      return this.activityContext?.propertyId || null;
    },
    primarySource() {
      const kind = this.activityContext?.kind;
      if (kind === KIND_LICENSE) return SOURCE_LICENSE;
      if (kind === KIND_PROPERTY) return SOURCE_PROPERTY;
      if (kind === KIND_LISTING) return SOURCE_LISTINGS;
      return null;
    },
    hasLinkedProperty() {
      return !!this.propertyId;
    },
    sourceOptions() {
      return this.sourceOptionConfig.map(source => ({
        ...source,
        selected: !!this.selectedSources[source.key],
        loading: !!this.sourceLoading[source.key],
        count: this.sourceComments[source.key]?.length || 0
      }));
    },
    sourceOptionConfig() {
      const kind = this.activityContext?.kind;
      if (kind === KIND_PROPERTY) {
        return [
          {
            key: SOURCE_PROPERTY,
            label: 'This Property',
            variant: 'primary',
            icon: 'house',
            available: this.isSourceAvailable(SOURCE_PROPERTY)
          },
          {
            key: SOURCE_LISTINGS,
            label: 'Listings',
            variant: 'purple',
            icon: 'link-45deg',
            available: this.isSourceAvailable(SOURCE_LISTINGS)
          },
          {
            key: SOURCE_LICENSE,
            label: 'Licenses',
            variant: 'success',
            icon: 'file-earmark-check',
            available: this.isSourceAvailable(SOURCE_LICENSE)
          }
        ];
      }
      if (kind === KIND_LISTING) {
        return [
          {
            key: SOURCE_LISTINGS,
            label: this.hasLinkedProperty ? 'Listings' : 'This Listing',
            variant: 'purple',
            icon: 'link-45deg',
            available: this.isSourceAvailable(SOURCE_LISTINGS)
          },
          {
            key: SOURCE_PROPERTY,
            label: 'Property',
            variant: 'primary',
            icon: 'house',
            available: this.isSourceAvailable(SOURCE_PROPERTY)
          },
          {
            key: SOURCE_LICENSE,
            label: 'Licenses',
            variant: 'success',
            icon: 'file-earmark-check',
            available: this.isSourceAvailable(SOURCE_LICENSE)
          }
        ];
      }
      if (kind === KIND_LICENSE) {
        return [
          {
            key: SOURCE_LICENSE,
            label: 'This License',
            variant: 'success',
            icon: 'file-earmark-check',
            available: this.isSourceAvailable(SOURCE_LICENSE)
          },
          {
            key: SOURCE_PROPERTY,
            label: 'Property',
            variant: 'primary',
            icon: 'house',
            available: this.isSourceAvailable(SOURCE_PROPERTY)
          },
          {
            key: SOURCE_LISTINGS,
            label: 'Listings',
            variant: 'purple',
            icon: 'link-45deg',
            available: this.isSourceAvailable(SOURCE_LISTINGS)
          }
        ];
      }
      return [];
    },
    selectedSourceKeys() {
      return Object.keys(this.selectedSources)
      .filter(key => this.selectedSources[key] && this.isSourceAvailable(key));
    },
    typeOptions() {
      return [
        {
          key: TYPE_STANDALONE,
          label: 'Comments',
          icon: 'chat'
        },
        {
          key: TYPE_VERIFICATION,
          label: 'Verifications',
          icon: 'check-circle'
        },
        {
          key: TYPE_CORRESPONDENCE,
          label: 'Letters',
          icon: 'envelope'
        },
        {
          key: TYPE_LICENSE_EDIT,
          label: 'License Edits',
          icon: 'pencil-square'
        },
        {
          key: TYPE_DEFERRAL,
          label: 'Deferrals',
          icon: 'clock'
        }
      ].map(type => ({
        ...type,
        selected: !!this.selectedTypes[type.key]
      }));
    },
    selectedTypeKeys() {
      return Object.keys(this.selectedTypes).filter(key => this.selectedTypes[key]);
    },
    visibleComments() {
      const selectedTypes = new Set(this.selectedTypeKeys);
      const comments = this.selectedSourceKeys
      .flatMap(source => this.sourceComments[source] || [])
      .filter(item => selectedTypes.has(item.comment.type));
      comments.sort((a, b) => b.createdAtMs - a.createdAtMs);
      return comments;
    },
    isLoadingVisibleComments() {
      if (this.visibleComments.length > 0) return false;
      return this.selectedSourceKeys.some(source => this.sourceLoading[source]);
    },
    emptyStateText() {
      if (this.selectedSourceKeys.length === 0) {
        return 'Select at least one activity source';
      }
      if (this.selectedTypeKeys.length === 0) {
        return 'Select at least one activity type';
      }
      return 'No activity';
    }
  },
  methods: {
    async resetAndLoad() {
      Object.assign(this.$data, DEFAULT_DATA());
      await this.ensureSelectedSourcesLoaded();
    },
    async toggleSource(source) {
      if (!this.isSourceAvailable(source)) return;
      this.selectedSources = {
        ...this.selectedSources,
        [source]: !this.selectedSources[source]
      };
      if (this.selectedSources[source]) {
        await this.ensureSourceLoaded(source, this.loadToken);
      }
    },
    toggleType(type) {
      this.selectedTypes = {
        ...this.selectedTypes,
        [type]: !this.selectedTypes[type]
      };
    },
    isSourceAvailable(source) {
      const kind = this.activityContext?.kind;
      if (!kind || !this.primaryModel?.id) return false;
      if (source === SOURCE_LICENSE) {
        return kind === KIND_LICENSE || !!this.propertyId;
      }
      if (source === SOURCE_PROPERTY) {
        return kind === KIND_PROPERTY || !!this.propertyId;
      }
      if (source === SOURCE_LISTINGS) {
        return kind === KIND_LISTING || !!this.propertyId;
      }
      return false;
    },
    async ensureSelectedSourcesLoaded() {
      for (const {key: source} of this.sourceOptionConfig) {
        if (this.selectedSources[source]) {
          const token = ++this.loadToken;
          await this.ensureSourceLoaded(source, token);
        }
      }
    },
    async ensureSourceLoaded(source, token) {
      if (!this.isSourceAvailable(source)) return;
      if (this.sourceLoaded[source] || this.sourceLoading[source]) return;

      this.sourceLoading = {
        ...this.sourceLoading,
        [source]: true
      };

      try {
        let comments = [];
        if (source === SOURCE_LICENSE) {
          comments = await this.loadLicenseComments(token);
        } else if (source === SOURCE_PROPERTY) {
          comments = await this.loadPropertyComments(token);
        } else if (source === SOURCE_LISTINGS) {
          comments = await this.loadListingComments(token);
        }

        if (token !== this.loadToken) return;

        this.sourceComments = {
          ...this.sourceComments,
          [source]: comments
        };
        this.sourceLoaded = {
          ...this.sourceLoaded,
          [source]: true
        };
      } catch (error) {
        console.error(`Could not load ${source} activity`, error);
      } finally {
        if (token === this.loadToken) {
          this.sourceLoading = {
            ...this.sourceLoading,
            [source]: false
          };
        }
      }
    },
    async loadLicenseComments(token) {
      const context = this.activityContext;
      if (!context) return [];

      if (context.kind === KIND_LICENSE) {
        const license = context.primary;
        const comments = await Comment.list(license);
        if (token !== this.loadToken) return [];
        this.licenseLookup[license.id] = license;
        return comments.map(comment => this.normalizeComment({
          comment,
          source: SOURCE_LICENSE,
          sourceId: license.id
        }));
      }

      if (!this.propertyId || !this.city) return [];

      const response = await License.query(this.city, {
        constraints: [['propertyId', '==', this.propertyId]]
      });
      if (token !== this.loadToken) return [];

      const licenses = response?.results || [];
      licenses.forEach(license => {
        this.licenseLookup[license.id] = license;
      });

      const comments = [];
      await this.mapWithConcurrency(licenses, 6, async license => {
        const licenseComments = await Comment.list(license);
        licenseComments.forEach(comment => {
          comments.push(this.normalizeComment({
            comment,
            source: SOURCE_LICENSE,
            sourceId: license.id
          }));
        });
      });

      return token === this.loadToken ? comments : [];
    },
    async loadPropertyComments(token) {
      const property = await this.resolveProperty(token);
      if (!property) return [];

      const comments = await Comment.list(property);
      if (token !== this.loadToken) return [];
      this.propertyLookup[property.id] = property;
      return comments.map(comment => this.normalizeComment({
        comment,
        source: SOURCE_PROPERTY,
        sourceId: property.id
      }));
    },
    async loadListingComments(token) {
      const listings = await this.resolveListings(token);
      listings.forEach(listing => {
        this.listingLookup[listing.id] = listing;
      });

      const comments = [];
      await this.mapWithConcurrency(listings, 6, async listing => {
        const listingComments = await Comment.list(listing);
        listingComments.forEach(comment => {
          comments.push(this.normalizeComment({
            comment,
            source: SOURCE_LISTINGS,
            sourceId: listing.id
          }));
        });
      });

      return token === this.loadToken ? comments : [];
    },
    async resolveProperty(token) {
      const context = this.activityContext;
      if (!context) return null;
      if (context.kind === KIND_PROPERTY) return context.primary;
      if (context.property) return context.property;
      if (!this.propertyId || !this.city) return null;

      const property = await Property.one(this.city, this.propertyId);
      if (token !== this.loadToken) return null;
      return property;
    },
    async resolveListings(token) {
      const context = this.activityContext;
      if (!context) return [];
      if (context.kind === KIND_LISTING && !this.propertyId) {
        return [context.primary];
      }
      if (!this.propertyId || !this.city) return [];

      const response = await Listing.query(this.city, {
        constraints: [['verifiedPropertyId', '==', this.propertyId]]
      });
      if (token !== this.loadToken) return [];

      const listings = response?.results || [];
      if (context.kind === KIND_LISTING && !listings.some(listing => listing.id === context.primary.id)) {
        listings.unshift(context.primary);
      }
      return this.uniqueModels(listings);
    },
    uniqueModels(models) {
      const seen = new Set();
      return models.filter(model => {
        if (!model?.id || seen.has(model.id)) return false;
        seen.add(model.id);
        return true;
      });
    },
    async mapWithConcurrency(items, concurrency, task) {
      if (!items.length) return;
      let index = 0;
      const workers = new Array(Math.min(concurrency, items.length)).fill(null).map(async () => {
        while (index < items.length) {
          const currentIndex = index;
          index += 1;
          const item = items[currentIndex];
          try {
            await task(item, currentIndex);
          } catch (error) {
            console.error('Activity source load failed', error);
          }
        }
      });
      await Promise.all(workers);
    },
    normalizeComment({ comment, source, sourceId }) {
      return {
        key: `${source}:${sourceId}:${comment.id}`,
        source,
        sourceId,
        comment,
        createdAtMs: this.toMillis(comment.createdAt),
        sourceBadge: this.buildSourceBadge(source, sourceId)
      };
    },
    buildSourceBadge(source, sourceId) {
      if (source === SOURCE_LICENSE) {
        const license = this.licenseLookup[sourceId] || this.getPrimaryModelForSource(source, sourceId);
        return {
          type: 'license',
          title: license?.number ?? license?.id ?? sourceId,
          icon: 'file-earmark-check',
          badgeClass: 'text-bg-success text-success border-success'
        };
      }
      if (source === SOURCE_PROPERTY) {
        const property = this.propertyLookup[sourceId] || this.getPrimaryModelForSource(source, sourceId) || this.activityContext?.property;
        return {
          type: 'property',
          title: normalizeAddressCasing(property?.addressFormatted || sourceId),
          icon: 'house',
          badgeClass: 'text-bg-primary text-primary border-primary'
        };
      }
      const listing = this.listingLookup[sourceId] || this.getPrimaryModelForSource(source, sourceId);
      return {
        type: 'listing',
        title: listing?.listingTitle ?? listing?.title ?? listing?.id ?? sourceId,
        icon: 'geo-alt',
        badgeClass: 'text-bg-purple text-purple border-purple'
      };
    },
    getPrimaryModelForSource(source, sourceId) {
      return this.primarySource === source && this.primaryModel?.id === sourceId
        ? this.primaryModel
        : null;
    },
    toMillis(value) {
      if (!value) return 0;
      if (typeof value.toDate === 'function') {
        return value.toDate().getTime();
      }
      if (value instanceof Date) {
        return value.getTime();
      }
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? 0 : date.getTime();
    },
    async commit(newMessage) {
      if (!this.primaryModel || !this.primarySource) return;
      const comment = await Comment.create(this.primaryModel, newMessage);
      const normalized = this.normalizeComment({
        comment,
        source: this.primarySource,
        sourceId: this.primaryModel.id
      });
      this.storeSourceModel(this.primarySource, this.primaryModel);
      this.sourceComments = {
        ...this.sourceComments,
        [this.primarySource]: [normalized, ...(this.sourceComments[this.primarySource] || [])]
      };
      this.sourceLoaded = {
        ...this.sourceLoaded,
        [this.primarySource]: true
      };
    },
    storeSourceModel(source, model) {
      if (!model?.id) return;
      if (source === SOURCE_LICENSE) {
        this.licenseLookup[model.id] = model;
      } else if (source === SOURCE_PROPERTY) {
        this.propertyLookup[model.id] = model;
      } else if (source === SOURCE_LISTINGS) {
        this.listingLookup[model.id] = model;
      }
    },
    onScroll(event) {
      this.scrollTop = event.target.scrollTop;
    },
    onDeleted(item) {
      this.sourceComments = {
        ...this.sourceComments,
        [item.source]: (this.sourceComments[item.source] || []).filter(existing => existing.key !== item.key)
      };
    }
  }
};
</script>

<style scoped>
.section-title {
  font-size: 0.75rem;
  font-weight: 600;
  opacity: 0.75;
  text-transform: uppercase;
  color: var(--bs-secondary-color);
}
.section-filter-toggle {
  border: 1px solid #e7e5e4 !important;
  background-color: #fafaf9;
  color: #78716c;
}
.section-filter-toggle.selected, .section-filter-toggle:hover {
  border: 1px solid #a8a29e;
  background-color: #ffffff;
  color: #1c1917;
}
.section-filter-title {
  color: #a8a29e;
  min-width: 44px;
  flex-shrink: 0;
  font-weight: 500;
  font-size: 0.75rem;
  line-height: 26px;
}
.comment-source-pills {
  background-color: var(--bs-light);
}
.comment-type-pills {
  background-color: var(--bs-light);
}
.comment-filter-pill.selected, .comment-filter-pill:hover {
  background-color: #ffffff;
  border: 1px solid #A8A29F;
  color: #1c1917;
}
.comment-filter-pill {
  background-color: #ffffff;
  border: 1px solid #e7e5e4;
  color: #a8a29e;
  border-radius: 13px;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  line-height: 16px;
  font-weight: 400;
  font-size: 0.75rem;
}
.new-comment {
  z-index: 1;
  transition: border-color 250ms;
  margin-top: 0.75rem;
}
.new-comment.focused {
  border-color: var(--bs-primary) !important;
}
</style>
